import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/stores/auth';
import { useIsAdmin } from '@/lib/admin';
import { FEATURES, normalizePlan, type BillingInterval, type PlanId } from '@/lib/plans';

export interface PlanInfo {
  plan: PlanId;
  status: string;
  interval: string | null;
  periodEnd: string | null;
  hasBilling: boolean;
  /** Admins get every feature regardless of plan (the DB triggers agree). */
  isAdmin: boolean;
  canShop: boolean;
  canUnlimitedCards: boolean;
}

/** The signed-in user's plan + entitlements, from their own profile row. */
export function usePlan() {
  const userId = useAuth((s) => s.user?.id);
  const { isAdmin } = useIsAdmin();
  const query = useQuery({
    queryKey: ['plan', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('plan, plan_status, plan_interval, current_period_end, stripe_customer_id')
        .eq('id', userId!)
        .maybeSingle();
      return data;
    },
    staleTime: 60_000,
  });
  const plan = normalizePlan(query.data?.plan);
  const info: PlanInfo = {
    plan,
    status: query.data?.plan_status ?? 'none',
    interval: query.data?.plan_interval ?? null,
    periodEnd: query.data?.current_period_end ?? null,
    hasBilling: !!query.data?.stripe_customer_id,
    isAdmin,
    canShop: isAdmin || FEATURES.shop(plan),
    canUnlimitedCards: isAdmin || FEATURES.unlimitedCards(plan),
  };
  return { ...info, isLoading: query.isLoading, refetch: query.refetch };
}

/** Bearer-authenticated call that returns a Stripe URL to redirect to. */
async function billingCall(path: string, body: Record<string, unknown>): Promise<string> {
  const { data: session } = await supabase.auth.getSession();
  const token = session.session?.access_token;
  if (!token) throw new Error('Sign in first');
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const out = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!res.ok || !out.url) throw new Error(out.error ?? 'Billing request failed');
  return out.url;
}

export function startCheckout(plan: PlanId, interval: BillingInterval): Promise<string> {
  return billingCall('/api/billing/checkout', { plan, interval });
}

export function openBillingPortal(): Promise<string> {
  return billingCall('/api/billing/portal', {});
}
